import { useState } from 'react';

export const useRecipeImport = ({ inventory, setRecipeData }) => {
  const [tabValue, setTabValue] = useState(0);
  const [pasteContent, setPasteContent] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedLines, setParsedLines] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
    setShowComparison(false);
    setIsProcessing(false);
    if (newValue === 0) {
      setPasteContent('');
    } else {
      setImageFile(null);
      setImagePreview('');
    }
  };

  // Attempt to match an ingredient in the inventory
  const findClosestIngredient = (ingredientName, inventoryList) => {
    const searchName = ingredientName.toLowerCase().trim();

    // Exact match
    const exactMatch = inventoryList.find(item => item.name.toLowerCase() === searchName);
    if (exactMatch) return exactMatch;

    // Partial match
    const partialMatches = inventoryList
      .map(item => {
        const itemName = item.name.toLowerCase();
        const containsSearch = itemName.includes(searchName);
        const containsItem = searchName.includes(itemName);
        if (containsSearch || containsItem) {
          const lengthDiff = Math.abs(itemName.length - searchName.length);
          const overlap = Math.min(itemName.length, searchName.length);
          const score = overlap - lengthDiff;
          return { item, score };
        }
        return null;
      })
      .filter(match => match !== null)
      .sort((a, b) => b.score - a.score);

    if (partialMatches.length > 0) return partialMatches[0].item;
    return null;
  };

  // Handle pasting recipe text
  const handlePasteSubmit = async () => {
    if (!pasteContent.trim()) {
      setError('Please paste a recipe first');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('https://www.crafttiki.com/api/process-recipe.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_BACKEND_API_KEY
        },
        body: JSON.stringify({ recipeText: pasteContent }),
      });
      if (!response.ok) {
        throw new Error('Failed to process recipe');
      }
      const processedRecipe = await response.json();

      if (!processedRecipe.lines || processedRecipe.lines.length === 0) {
        throw new Error('No recipe lines were parsed');
      }

      // Assign fuzzy-matched names
      const updatedParsedLines = processedRecipe.lines.map(line => {
        if (line.type === 'ingredient') {
          const matchedIngredient = findClosestIngredient(line.parsed.name, inventory);
          const amount = line.parsed.amount !== undefined ? String(line.parsed.amount) : '';
          return {
            ...line,
            parsed: {
              ...line.parsed,
              amount,
              name: matchedIngredient ? matchedIngredient.name : line.parsed.name,
              ingredientId: matchedIngredient ? matchedIngredient.id : ''
            }
          };
        }
        return line;
      });

      setParsedLines(updatedParsedLines);
      setShowComparison(true);

      // Prepare recipe data
      const formattedIngredients = updatedParsedLines
        .filter(l => l.type === 'ingredient')
        .map(l => ({
          name: l.parsed.name,
          amount: l.parsed.amount ? String(l.parsed.amount) : '1',
          unit: l.parsed.unit || 'oz',
          ingredientId: l.parsed.ingredientId || ''
        }));

      const newRecipeData = {
        name: updatedParsedLines.find(l => l.type === 'name')?.parsed.value || '',
        origin: 'My Recipes',
        description: updatedParsedLines
          .filter(l => l.type === 'description')
          .map(l => l.parsed.value)
          .join('\n'),
        ingredients: formattedIngredients,
        instructions: updatedParsedLines
          .filter(l => l.type === 'instruction')
          .map(l => l.parsed.value)
          .join('\n'),
        garnish: updatedParsedLines
          .filter(l => l.type === 'garnish')
          .map(l => l.parsed.value)
          .join(', '),
        glass: updatedParsedLines
          .filter(l => l.type === 'glass')
          .map(l => l.parsed.value)
          .join(', ')
      };

      setRecipeData(newRecipeData);

    } catch (err) {
      console.error('Error in handlePasteSubmit:', err);
      setError(err.message || 'Failed to process recipe. Please try again.');
      setShowComparison(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Process image for recipe extraction
  const handleImageSubmit = async () => {
    if (!imageFile) {
      setError('Please upload an image first');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      // Convert image to base64
      const reader = new FileReader();
      const imageBase64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });
      const imageBase64 = await imageBase64Promise;
      console.log("Image converted to base64, length:", imageBase64.length);

      try {
        console.log("Calling API endpoint...");
        const response = await fetch('https://www.crafttiki.com/api/extract-recipe-from-image.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_BACKEND_API_KEY
          },
          body: JSON.stringify({ imageData: imageBase64 }),
        });
        
        // Get the response text first for debugging
        const responseText = await response.text();
        console.log("Raw API Response:", responseText);
        
        // Try to parse it as JSON
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error("Failed to parse response as JSON:", parseError);
          throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
        }

        if (!response.ok) {
          console.error("API Error:", data);
          throw new Error(data.error || `Server error: ${response.status}`);
        }
        
        if (!data.success || !data.lines || data.lines.length === 0) {
          throw new Error('No recipe content was extracted from the image');
        }

        console.log("Extracted recipe data:", data);
        processExtractedRecipe({ lines: data.lines });
        setSuccess('Recipe extracted successfully!');
      } catch (apiError) {
        console.error("API call failed:", apiError);
        setError(`Failed to process image: ${apiError.message}`);
        setShowComparison(false);
      }
    } catch (err) {
      console.error('Error in handleImageSubmit:', err);
      setError('Failed to process image. Please try again.');
      setShowComparison(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper to handle the lines extracted from an image
  const processExtractedRecipe = (extractedRecipe) => {
    console.log("Processing extracted recipe:", extractedRecipe);
    
    if (!extractedRecipe.lines || !Array.isArray(extractedRecipe.lines) || extractedRecipe.lines.length === 0) {
      console.error("Invalid or empty recipe data:", extractedRecipe);
      setError("Received invalid recipe data from the server.");
      return;
    }
    
    const updatedParsedLines = extractedRecipe.lines.map(line => {
      if (line.type === 'ingredient') {
        const matchedIngredient = findClosestIngredient(line.parsed.name, inventory);
        const amount = line.parsed.amount !== undefined ? String(line.parsed.amount) : '';
        return {
          ...line,
          parsed: {
            ...line.parsed,
            amount,
            name: matchedIngredient ? matchedIngredient.name : line.parsed.name,
            ingredientId: matchedIngredient ? matchedIngredient.id : ''
          }
        };
      }
      return line;
    });

    setParsedLines(updatedParsedLines);
    setShowComparison(true);

    const formattedIngredients = updatedParsedLines
      .filter(line => line.type === 'ingredient')
      .map(line => ({
        name: line.parsed.name,
        amount: line.parsed.amount ? String(line.parsed.amount) : '1',
        unit: line.parsed.unit || 'oz',
        ingredientId: line.parsed.ingredientId || ''
      }));

    const newRecipeData = {
      name: updatedParsedLines.find(l => l.type === 'name')?.parsed.value || '',
      origin: 'My Recipes',
      description: updatedParsedLines
        .filter(l => l.type === 'description')
        .map(l => l.parsed.value)
        .join('\n'),
      ingredients: formattedIngredients,
      instructions: updatedParsedLines
        .filter(l => l.type === 'instruction')
        .map(l => l.parsed.value)
        .join('\n'),
      garnish: updatedParsedLines
        .filter(l => l.type === 'garnish')
        .map(l => l.parsed.value)
        .join(', '),
      glass: updatedParsedLines
        .filter(l => l.type === 'glass')
        .map(l => l.parsed.value)
        .join(', ')
    };

    setRecipeData(newRecipeData);
  };

  return {
    tabValue,
    pasteContent,
    setPasteContent,
    isProcessing,
    parsedLines,
    showComparison,
    setShowComparison,
    imageFile,
    imagePreview,
    error,
    setError,
    success,
    setSuccess,
    handleTabChange,
    handlePasteSubmit,
    handleImageUpload,
    handleImageSubmit,
  };
}; 